import { storageService } from './storageService';

export interface ProfilePersistenceData {
  isComplete: boolean;
  lastUpdated: number;
  profileData?: any;
  phoneVerified?: boolean;
}

class ProfilePersistenceService {
  private readonly PROFILE_KEY = 'hydro_profile_data';
  private readonly COMPLETION_KEY = 'hydro_profile_complete';
  private readonly PHONE_VERIFIED_KEY = 'hydro_phone_verified';

  /**
   * Save profile completion status to local storage
   */
  saveProfileCompletion(isComplete: boolean, profileData?: any): void {
    try {
      const persistenceData: ProfilePersistenceData = {
        isComplete,
        lastUpdated: Date.now(),
        profileData,
        phoneVerified: profileData?.isPhoneVerified || false,
      };

      storageService.setItem(this.PROFILE_KEY, JSON.stringify(persistenceData));
      storageService.setItem(this.COMPLETION_KEY, isComplete.toString());

      if (profileData?.isPhoneVerified) {
        storageService.setItem(this.PHONE_VERIFIED_KEY, 'true');
      }

      console.log('📝 Profile completion saved to storage:', persistenceData);
    } catch (error) {
      console.error('Failed to save profile completion:', error);
    }
  }

  /**
   * Get profile completion status from local storage
   */
  getProfileCompletion(): ProfilePersistenceData | null {
    try {
      const data = storageService.getItem(this.PROFILE_KEY);
      if (!data) return null;

      const parsed = JSON.parse(data) as ProfilePersistenceData;

      // Check if data is stale (older than 24 hours)
      const isStale = Date.now() - parsed.lastUpdated > 24 * 60 * 60 * 1000;
      if (isStale) {
        console.log('📝 Profile data is stale, clearing...');
        this.clearProfileData();
        return null;
      }

      return parsed;
    } catch (error) {
      console.error('Failed to get profile completion:', error);
      return null;
    }
  }

  /**
   * Check if profile setup is complete (quick check)
   */
  isProfileComplete(): boolean {
    try {
      const completion = storageService.getItem(this.COMPLETION_KEY);
      return completion === 'true';
    } catch (error) {
      console.error('Failed to check profile completion:', error);
      return false;
    }
  }

  /**
   * Check if phone is verified (quick check)
   */
  isPhoneVerified(): boolean {
    try {
      const verified = storageService.getItem(this.PHONE_VERIFIED_KEY);
      return verified === 'true';
    } catch (error) {
      console.error('Failed to check phone verification:', error);
      return false;
    }
  }

  /**
   * Clear all profile data from storage
   */
  clearProfileData(): void {
    try {
      storageService.removeItem(this.PROFILE_KEY);
      storageService.removeItem(this.COMPLETION_KEY);
      storageService.removeItem(this.PHONE_VERIFIED_KEY);

      // Also clear legacy keys
      storageService.removeItem('profileSetupCompleted');
      storageService.removeItem('profileSetupTimestamp');

      console.log('📝 Profile data cleared from storage');
    } catch (error) {
      console.error('Failed to clear profile data:', error);
    }
  }

  /**
   * Update phone verification status
   */
  updatePhoneVerification(isVerified: boolean): void {
    try {
      storageService.setItem(this.PHONE_VERIFIED_KEY, isVerified.toString());

      // Update the main profile data if it exists
      const profileData = this.getProfileCompletion();
      if (profileData) {
        profileData.phoneVerified = isVerified;
        profileData.lastUpdated = Date.now();
        storageService.setItem(this.PROFILE_KEY, JSON.stringify(profileData));
      }

      console.log('📝 Phone verification status updated:', isVerified);
    } catch (error) {
      console.error('Failed to update phone verification:', error);
    }
  }

  /**
   * Sync profile data with server response
   */
  syncWithServerData(serverProfile: any): void {
    try {
      const isComplete = !!(
        serverProfile.firstName &&
        serverProfile.lastName &&
        serverProfile.avatarName &&
        serverProfile.phoneNumber &&
        serverProfile.isPhoneVerified
      );

      this.saveProfileCompletion(isComplete, serverProfile);
    } catch (error) {
      console.error('Failed to sync with server data:', error);
    }
  }

  /**
   * Get profile setup redirect path based on completion status
   */
  getRedirectPath(): string {
    const profileData = this.getProfileCompletion();

    if (!profileData || !profileData.isComplete) {
      return '/onboarding';
    }

    return '/dashboard';
  }
}

export const profilePersistence = new ProfilePersistenceService();
